import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SelectionModal from './SelectionModal';
import ComparisonModal from './ComparisonModal';
import RecommendationModal from './RecommendationModal';
import useComparisonStore from './store/comparisonStore';

/**
 * ComparisonModalManager component
 *
 * Uses Zustand for state management to ensure:
 * 1. Only the selection modal can open the comparison modal
 * 2. The comparison modal only opens when there are selections
 * 3. All modal state is managed in a single place
 */
const ComparisonModalManager = ({
  isOpen = false,
  onClose = () => {},
  initialOperators = [],
  onSelectPackage = () => {},
  openCoverageSidebar = null,
  startWithRecommendation = false
}) => {
  const navigate = useNavigate();
  
  // Get state and actions from the Zustand store
  const {
    isSelectionModalOpen,
    isComparisonModalOpen,
    isRecommendationModalOpen,
    selectedOperators,
    openSelectionModal,
    openRecommendationModal,
    openRecommendationModalOnly,
    closeAllModals,
    setSelectedOperators,
    selectAllOperators,
    handleRecommendationComplete
  } = useComparisonStore();
  
  // Initialize state based on props - with a ref to prevent infinite loops
  const initializedRef = React.useRef(false);
  
  // Use a ref to track if we've already initialized to prevent double initialization
  const hasInitializedRef = React.useRef(false);
  
  useEffect(() => {
    // Only run this effect when isOpen changes from false to true and we haven't initialized yet
    if (isOpen && !hasInitializedRef.current) {
      console.log("ComparisonModalManager: Initializing modals");
      hasInitializedRef.current = true;
      
      // DO NOT close all modals here - this was causing the immediate closing issue
      
      if (startWithRecommendation) {
        // If we're starting with recommendation, only open the recommendation modal
        console.log("Preparing to open ONLY recommendation modal from ComparisonModalManager");
        
        // Set a flag to prevent other modals from opening
        localStorage.setItem('recommendationJustOpened', 'true');
        
        // Open the recommendation modal directly without setTimeout
        openRecommendationModal();
        console.log("Opening ONLY recommendation modal from ComparisonModalManager");
      } else {
        // Otherwise, open the selection modal with the initial operators
        const params = {};
        
        if (initialOperators && initialOperators.length > 0) {
          console.log('Setting initial operators:', initialOperators);
          
          if (initialOperators[0] === 'all') {
            params.selectAllOperators = true;
          } else {
            // Set the selected operators directly
            setSelectedOperators(initialOperators);
          }
        }
        
        // Open the selection modal directly without setTimeout
        openSelectionModal(params);
        console.log("Opening selection modal from ComparisonModalManager");
      }
    } else if (!isOpen) {
      // Reset the initialization flag when isOpen becomes false
      hasInitializedRef.current = false;
      
      // Only close modals if we're explicitly told to close (isOpen becomes false)
      if (initializedRef.current) {
        initializedRef.current = false;
        closeAllModals();
      }
    }
  }, [isOpen]); // Only depend on isOpen to prevent unnecessary re-runs
  
  // Handle package selection
  const handleSelectPackage = (pkg, action = 'availability') => {
    onSelectPackage(pkg);
    
    if (action === 'availability') {
      console.log('Opening coverage sidebar from ComparisonModalManager');
      
      // Close all modals
      closeAllModals();
      
      // Call the parent onClose
      onClose();
      
      // Use the provided function or navigate as fallback
      if (openCoverageSidebar && typeof openCoverageSidebar === 'function') {
        setTimeout(() => openCoverageSidebar(), 300);
      } else {
        navigate('/coverage');
      }
    }
  };
  
  // Handle close
  const handleClose = () => {
    console.log("ComparisonModalManager: handleClose called");
    
    // Get current modal states
    const { isRecommendationModalOpen, isSelectionModalOpen, isComparisonModalOpen } = useComparisonStore.getState();
    console.log("ComparisonModalManager: Modal states before closing:", {
      isRecommendationModalOpen,
      isSelectionModalOpen,
      isComparisonModalOpen
    });
    
    // Check if the recommendation modal was just opened
    const recommendationJustOpened = localStorage.getItem('recommendationJustOpened') === 'true';
    
    if (recommendationJustOpened && isRecommendationModalOpen) {
      console.log("ComparisonModalManager: Preventing close of recommendation modal that was just opened");
      return; // Don't close if recommendation was just opened
    }
    
    // Call the parent onClose first to ensure proper state updates
    onClose();
    
    // Then close all modals
    setTimeout(() => {
      closeAllModals();
    }, 50);
  };
  
  return (
    <>
      {/* Selection Modal */}
      <SelectionModal
        isOpen={isSelectionModalOpen}
        onClose={handleClose}
      />
      
      {/* Comparison Modal */}
      <ComparisonModal
        isOpen={isComparisonModalOpen}
        onClose={handleClose}
        onSelectPackage={handleSelectPackage}
      />
      
      {/* Recommendation Modal */}
      <RecommendationModal
        isOpen={isRecommendationModalOpen}
        onClose={handleClose}
        onComplete={handleRecommendationComplete}
      />
    </>
  );
};

export default ComparisonModalManager;

// Export a hook for external components to use the modal manager
export const useModalManager = () => {
  const {
    openSelectionModal,
    openComparisonModal,
    openRecommendationModal,
    openRecommendationModalOnly,
    closeAllModals,
    initializeAvailableOperators
  } = useComparisonStore();
  
  // Initialize available operators when hook is used
  useEffect(() => {
    initializeAvailableOperators();
  }, [initializeAvailableOperators]);
  
  return {
    openSelectionModal: (params = {}) => {
      // Set a flag to indicate we're opening the selection modal
      localStorage.setItem('selectionModalOpening', 'true');
      
      // Open the selection modal directly without closing others first
      openSelectionModal(params);
      
      // Clear the flag after a short delay
      setTimeout(() => {
        localStorage.removeItem('selectionModalOpening');
      }, 500);
    },
    openComparisonModal,
    openRecommendationModal: () => {
      // Set a flag to prevent other modals from opening
      localStorage.setItem('recommendationJustOpened', 'true');
      
      // Open the recommendation modal directly without closing others first
      openRecommendationModal();
      console.log("Opening ONLY recommendation modal from useModalManager");
      
      // Clear the flag after a delay
      setTimeout(() => {
        localStorage.removeItem('recommendationJustOpened');
      }, 1000);
    },
    closeAllModals
  };
};